import { assert } from "#core/utils/assert.ts";

// A drop-in replacement for `WebSocket` with automatic reconnection using jittered exponential backoff.
export class RobustWebSocket extends EventTarget {
  static CONNECTING: typeof WebSocket.CONNECTING = WebSocket.CONNECTING;
  static OPEN: typeof WebSocket.OPEN = WebSocket.OPEN;
  static CLOSING: typeof WebSocket.CLOSING = WebSocket.CLOSING;
  static CLOSED: typeof WebSocket.CLOSED = WebSocket.CLOSED;

  #retryController = new AbortController();
  #args: ConstructorParameters<typeof WebSocket>;

  #socket: WebSocket;

  #attempt = 0;

  binaryType: WebSocket["binaryType"] = "blob";

  onopen: WebSocket["onopen"] = null;
  onmessage: WebSocket["onmessage"] = null;
  onerror: WebSocket["onerror"] = null;
  onclose: WebSocket["onclose"] = null;

  backoffBase = 2000;
  backoffCap = 60_000;

  constructor(...args: ConstructorParameters<typeof WebSocket>) {
    super();

    this.#args = args;

    this.#connect();

    // @ts-expect-error: initialization happens inside #connect
    //
    // See:
    // - <https://github.com/microsoft/TypeScript/issues/21132>
    // - <https://github.com/microsoft/TypeScript/issues/24446>
    // - <https://github.com/microsoft/TypeScript/issues/30462>
    // - <https://github.com/microsoft/TypeScript/issues/32194>
    // - <https://github.com/microsoft/TypeScript/issues/59997>
    assert(this.#socket instanceof WebSocket);
  }

  #connect(): void {
    this.#socket = new WebSocket(...this.#args);

    this.#socket.binaryType = this.binaryType;
    this.#socket.onopen = this.onopen;
    this.#socket.onmessage = this.onmessage;
    this.#socket.onerror = this.onerror;
    this.#socket.onclose = this.onclose;

    this.#socket.addEventListener("open", () => {
      this.#attempt = 0;
      this.dispatchEvent(new Event("open"));
    });

    this.#socket.addEventListener("message", (messageEvent) => {
      this.dispatchEvent(
        new MessageEvent("message", {
          data: messageEvent.data,
        }),
      );
    });

    this.#socket.addEventListener("close", (closeEvent) => {
      this.dispatchEvent(
        new CloseEvent("close", {
          code: closeEvent.code,
          reason: closeEvent.reason,
          wasClean: closeEvent.wasClean,
        }),
      );

      if (this.#retryController.signal.aborted) {
        return;
      }

      const backoff = new Promise((resolve) => {
        const delay = Math.min(
          Math.random() * this.backoffBase * 2 ** this.#attempt,
          this.backoffCap,
        );

        setTimeout(resolve, delay);
      });

      const abortion = new Promise((_, reject) => {
        this.#retryController.signal.addEventListener("abort", () => {
          reject();
        });
      });

      void Promise.race([backoff, abortion]).then(
        () => {
          this.#attempt += 1;
          this.#connect();
        },
        () => {
          // silently ignore backoff abortion
        },
      );
    });
  }

  close(
    ...args: Parameters<WebSocket["close"]>
  ): ReturnType<WebSocket["close"]> {
    this.#retryController.abort();
    this.#socket.close(...args);
  }

  send(...args: Parameters<WebSocket["send"]>): ReturnType<WebSocket["send"]> {
    return this.#socket.send(...args);
  }

  get url(): WebSocket["url"] {
    return this.#socket.url;
  }

  get readyState(): WebSocket["readyState"] {
    return this.#socket.readyState;
  }

  get bufferedAmount(): WebSocket["bufferedAmount"] {
    return this.#socket.bufferedAmount;
  }

  get extensions(): WebSocket["extensions"] {
    return this.#socket.extensions;
  }

  get protocol(): WebSocket["protocol"] {
    return this.#socket.protocol;
  }

  override addEventListener: {
    <K extends keyof WebSocketEventMap>(
      type: K,
      listener: (this: RobustWebSocket, ev: WebSocketEventMap[K]) => void,
      options?: boolean | AddEventListenerOptions,
    ): void;
    (
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | AddEventListenerOptions,
    ): void;
  } = super.addEventListener;

  override removeEventListener: {
    <K extends keyof WebSocketEventMap>(
      type: K,
      listener: (this: RobustWebSocket, ev: WebSocketEventMap[K]) => void,
      options?: boolean | EventListenerOptions,
    ): void;
    (
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | EventListenerOptions,
    ): void;
  } = super.removeEventListener;
}
